import {
  UpsertFlowStepResult,
  UpsertFlowStepResultTypenames,
} from '@varo/graphql-schema-types';
import { usePathname } from 'expo-router';
import { getIntentBaseOnboardingScreenName } from 'features/onboarding/utils/mapToERoute';
import { useEffect } from 'react';
import UpsertFlowStep from 'services/graphql/mutations/onboarding/UpsertFlowStep.mutation';
import { redirectToHomePage } from 'shared/helpers/backNavigation.helpers';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import useCancellableMutation from 'shared/hooks/useCancellableMutation';
import { isNotNil } from 'shared/utils/isNotNil/isNotNil';

const useUpsertFlowStep = () => {
  const pathname = usePathname();
  const { segmentClient } = useAnalyticsConfiguration();

  const [{ fetching, data, error }, submitUpsertFlowStep] =
    useCancellableMutation<{
      upsertFlowStep: UpsertFlowStepResult;
    }>(UpsertFlowStep);

  const upsertFlowStepData = data?.upsertFlowStep;

  const hasNoUpsertCurrentStep =
    upsertFlowStepData?.__typename ===
    UpsertFlowStepResultTypenames.NoStepFound;

  const isFlowCompleted =
    upsertFlowStepData?.__typename ===
    UpsertFlowStepResultTypenames.FlowCompleted;

  const upsertFlowStepErrorMessage =
    upsertFlowStepData?.__typename ===
    UpsertFlowStepResultTypenames.UpsertFlowStepError
      ? upsertFlowStepData.message
      : undefined;

  const hasUpsertFlowStepError =
    Boolean(upsertFlowStepErrorMessage) || isNotNil(error);

  useEffect(() => {
    if (isFlowCompleted || hasNoUpsertCurrentStep || hasUpsertFlowStepError) {
      if (hasUpsertFlowStepError) {
        const stackTrace = error?.graphQLErrors.flatMap(
          (err) => err.stack,
        )?.[0];

        void segmentClient.logEvent('Onboarding Flow Error Encountered', {
          category: 'onboarding',
          subcategory: 'intent_based_onboarding',
          error_description: upsertFlowStepErrorMessage || stackTrace,
          step_name: getIntentBaseOnboardingScreenName(pathname),
        });
      }
      redirectToHomePage();
    }
  }, [
    isFlowCompleted,
    hasNoUpsertCurrentStep,
    hasUpsertFlowStepError,
    error?.graphQLErrors,
    segmentClient,
    upsertFlowStepErrorMessage,
    pathname,
  ]);

  return {
    isUpsertFlowStepLoading: fetching || isFlowCompleted,
    submitUpsertFlowStep,
  };
};

export default useUpsertFlowStep;
